import { useState, useEffect, useMemo } from 'react';
import { X, UserPlus, AlertTriangle, CheckCircle, Clipboard, ClipboardCheck, Send, ShieldCheck, Lock, Link } from 'lucide-react';
import {
  createUser,
  CreateUserRequest,
  CreateUserResponse,
  getSubscriptions,
  getAvailableLicenses,
  AvailableLicense,
  purgeAndCreateUser,
  DuplicateUserError,
  DuplicateUserInfo,
  getTenantDomains,
  TenantDomain,
  syncClient,
  generateTAP,
  storeSecureCredentials,
  sendTAPEmail,
  sendSecureLinkEmail,
  sendUserTAP,
  getRingCentralStatus,
  TAP_ELIGIBLE_SKUS
} from '../../services/api';
import { Subscription } from '../../types';

interface ExistingUser {
  displayName: string;
  userPrincipalName: string;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  tenantId?: string;
  onUserCreated: () => void;
  existingUsers?: ExistingUser[];
}

// Generate a random password that meets Azure AD requirements
function generatePassword(): string {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%^&*';

  let password = '';
  // Ensure at least one of each required type
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  // Fill the rest with random chars
  const allChars = lowercase + uppercase + numbers + special;
  for (let i = 0; i < 8; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

// Generate username from first and last name
function generateUsername(firstName: string, lastName: string): string {
  const first = firstName.toLowerCase().replace(/[^a-z]/g, '');
  const last = lastName.toLowerCase().replace(/[^a-z]/g, '');
  if (first && last) {
    return `${first}.${last}`;
  } else if (first) {
    return first;
  } else if (last) {
    return last;
  }
  return '';
}

type CredentialMethod = 'tap' | 'secure-link' | 'plain' | null;

interface TAPData {
  temporaryAccessPass: string;
  expiresAt: string;
}

interface SecureLinkData {
  token: string;
  expiresAt: string;
}

export default function CreateUserModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  tenantId,
  onUserCreated,
  existingUsers = []
}: CreateUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [password, setPassword] = useState(generatePassword());
  const [selectedLicenseSkuId, setSelectedLicenseSkuId] = useState<string>('');
  const [selectedLicenseName, setSelectedLicenseName] = useState<string>('');
  const [forceChangePassword, setForceChangePassword] = useState(true);
  const [usageLocation, setUsageLocation] = useState('US');

  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [domainsError, setDomainsError] = useState<string | null>(null);

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [availableLicenses, setAvailableLicenses] = useState<AvailableLicense[]>([]);
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([]); // For TAP eligibility check (unfiltered)
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<CreateUserResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateUserInfo | null>(null);
  const [purging, setPurging] = useState(false);
  const [copied, setCopied] = useState(false);

  // Secure credential delivery state
  const [credentialMethod, setCredentialMethod] = useState<CredentialMethod>(null);
  const [tapData, setTapData] = useState<TAPData | null>(null);
  const [secureLinkData, setSecureLinkData] = useState<SecureLinkData | null>(null);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  // Email sending state
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhoneNumber, setRecipientPhoneNumber] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // RingCentral status for split-channel delivery
  const [ringCentralConnected, setRingCentralConnected] = useState(false);

  // Check if tenant has TAP-eligible licenses
  // Uses allSubscriptions (unfiltered) because TAP-eligible licenses like Entra ID P2
  // may not have msSkuId set but still grant TAP capability to the tenant
  const isTapEligible = useMemo(() => {
    // Check by msSkuPartNumber first
    const bySkuPart = allSubscriptions.some(sub =>
      sub.msSkuPartNumber && TAP_ELIGIBLE_SKUS.includes(sub.msSkuPartNumber)
    );
    if (bySkuPart) return true;

    // Also check by product name for Entra ID P2 / Azure AD Premium subscriptions
    // (msSkuPartNumber may not be set for some Pax8 subscriptions)
    const byProductName = allSubscriptions.some(sub => {
      const name = sub.productName?.toLowerCase() || '';
      return name.includes('entra id p1') ||
             name.includes('entra id p2') ||
             name.includes('azure ad premium') ||
             name.includes('microsoft 365 business premium') ||
             name.includes('microsoft 365 e3') ||
             name.includes('microsoft 365 e5') ||
             name.includes('enterprise mobility');
    });
    if (byProductName) return true;

    // Also check live license data (from MS Graph)
    return availableLicenses.some(lic => {
      const part = lic.msSkuPartNumber?.toUpperCase() || '';
      return TAP_ELIGIBLE_SKUS.includes(part);
    });
  }, [allSubscriptions, availableLicenses]);

  // User's selected credential delivery method
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<'tap' | 'secure-link'>('tap');

  // Auto-generate display name from first and last name
  const displayName = useMemo(() => {
    const parts = [firstName.trim(), lastName.trim()].filter(Boolean);
    return parts.join(' ');
  }, [firstName, lastName]);

  // Check for duplicate display names among existing users
  const duplicateNameMatches = useMemo(() => {
    if (!displayName || displayName.length < 2) return [];
    const nameLower = displayName.toLowerCase().trim();
    return existingUsers.filter(u => u.displayName.toLowerCase().trim() === nameLower);
  }, [displayName, existingUsers]);

  // Full email address
  const fullEmail = useMemo(() => {
    if (username && selectedDomain) {
      return `${username}@${selectedDomain}`;
    }
    return '';
  }, [username, selectedDomain]);

  // Load domains when modal opens
  useEffect(() => {
    if (isOpen && clientId) {
      setLoadingDomains(true);
      setDomainsError(null);
      getTenantDomains(clientId)
        .then(response => {
          setDomains(response.domains);
          // Auto-select default domain
          const defaultDomain = response.domains.find(d => d.isDefault);
          if (defaultDomain) {
            setSelectedDomain(defaultDomain.domain);
          } else if (response.domains.length > 0) {
            setSelectedDomain(response.domains[0].domain);
          }
        })
        .catch(err => {
          console.error('Failed to load domains:', err);
          setDomainsError(err instanceof Error ? err.message : 'Failed to load domains');
        })
        .finally(() => {
          setLoadingDomains(false);
        });
    }
  }, [isOpen, clientId]);

  // Load subscriptions and available licenses when modal opens
  useEffect(() => {
    if (isOpen && clientId) {
      setLoadingSubscriptions(true);

      // Fetch both: Cosmos subs (for TAP eligibility) and live MS Graph licenses (for assignment dropdown)
      Promise.all([
        getSubscriptions({ clientId }),
        getAvailableLicenses(clientId).catch(err => {
          console.warn('[CreateUser] Failed to fetch live licenses, falling back to Cosmos data:', err);
          return null;
        })
      ]).then(([subsResponse, licensesResponse]) => {
        // Store ALL subscriptions for TAP eligibility check (unfiltered)
        setAllSubscriptions(subsResponse.items);

        if (licensesResponse && licensesResponse.items.length > 0) {
          // Use live MS Graph data — always fresh and accurate
          const available = licensesResponse.items.filter(lic => lic.availableLicenses > 0);
          setAvailableLicenses(available);
          // Clear Cosmos-based subscriptions since we're using live data
          setSubscriptions([]);
        } else {
          // Fallback: use Cosmos subscription data (original approach)
          const activeSubs = subsResponse.items.filter(sub =>
            !!sub.msSkuId && sub.status === 'active'
          );
          const bySkuId = new Map<string, typeof activeSubs[0]>();
          for (const sub of activeSubs) {
            const skuId = sub.msSkuId!;
            const existing = bySkuId.get(skuId);
            if (!existing) {
              bySkuId.set(skuId, sub);
            } else {
              const existingSync = existing.lastSyncDate || '';
              const subSync = sub.lastSyncDate || '';
              if (subSync > existingSync) {
                bySkuId.set(skuId, sub);
              }
            }
          }
          const available = Array.from(bySkuId.values()).filter(sub => {
            const assigned = sub.assignedQuantity || 0;
            return sub.quantity > assigned;
          });
          setSubscriptions(available);
          setAvailableLicenses([]);
        }
      }).catch(err => {
        console.error('Failed to load subscriptions:', err);
      }).finally(() => {
        setLoadingSubscriptions(false);
      });
    }
  }, [isOpen, clientId]);

  // Check RingCentral status when modal opens
  useEffect(() => {
    if (isOpen) {
      getRingCentralStatus()
        .then(response => {
          setRingCentralConnected(response.connected);
        })
        .catch(err => {
          console.warn('Failed to get RingCentral status:', err);
          setRingCentralConnected(false);
        });
    }
  }, [isOpen]);

  // Auto-generate username when first/last name changes
  useEffect(() => {
    const generatedUsername = generateUsername(firstName, lastName);
    setUsername(generatedUsername);
  }, [firstName, lastName]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFirstName('');
      setLastName('');
      setUsername('');
      setPassword(generatePassword());
      setSelectedLicenseSkuId('');
      setSelectedLicenseName('');
      setForceChangePassword(true);
      setResult(null);
      setError(null);
      setDuplicateInfo(null);
      setCopied(false);
      // Reset credential state
      setCredentialMethod(null);
      setTapData(null);
      setSecureLinkData(null);
      setGeneratingCredentials(false);
      setCredentialError(null);
      // Reset email state
      setRecipientEmail('');
      setRecipientPhoneNumber('');
      setSendingEmail(false);
      setEmailSent(false);
      setEmailError(null);
      // Reset delivery method
      setSelectedDeliveryMethod('tap');
      // Reset subscriptions (will be reloaded)
      setAllSubscriptions([]);
    }
  }, [isOpen]);

  const handleLicenseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const skuId = e.target.value;
    setSelectedLicenseSkuId(skuId);
    if (skuId) {
      const sub = subscriptions.find(s => s.msSkuId === skuId);
      setSelectedLicenseName(sub?.productName.replace(/\[New Commerce Experience\]/gi, '').trim() || '');
    } else {
      setSelectedLicenseName('');
    }
  };

  // Generate secure credentials after user creation based on selected method
  const generateSecureCredentials = async (userId: string, userEmail: string) => {
    setGeneratingCredentials(true);
    setCredentialError(null);

    // Use the user's selected delivery method
    if (selectedDeliveryMethod === 'tap' && tenantId) {
      try {
        // First, ensure TAP is enabled in the tenant
        console.log('Enabling TAP policy in tenant...');
        const { enableTAP } = await import('../../services/api');
        const enableResult = await enableTAP(clientId);

        if (!enableResult.success && !enableResult.alreadyEnabled) {
          // TAP couldn't be enabled - permission issue
          const errorMsg = enableResult.needsReconsent
            ? 'TAP permission not granted. Customer needs to re-consent the app.'
            : `Could not enable TAP: ${enableResult.error || 'Unknown error'}`;
          console.warn(errorMsg);
          setCredentialError(errorMsg + ' Falling back to Secure Link.');
          await generateSecureLink(userEmail);
          return;
        }

        console.log('TAP policy enabled, generating TAP code...');
        const tapResponse = await generateTAP({
          tenantId,
          userId,
          userName: displayName,
          lifetimeInMinutes: 1440, // 24 hours
          isUsableOnce: true,
        });

        if (tapResponse.success && tapResponse.temporaryAccessPass) {
          setCredentialMethod('tap');
          setTapData({
            temporaryAccessPass: tapResponse.temporaryAccessPass,
            expiresAt: tapResponse.expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          });
          setGeneratingCredentials(false);
          return;
        }

        // TAP failed - fallback to secure link (never plain text)
        const errorMsg = tapResponse.tapNotEnabled
          ? 'TAP is not enabled in this tenant. Falling back to Secure Link.'
          : `TAP generation failed: ${tapResponse.error || 'Unknown error'}. Falling back to Secure Link.`;
        console.warn(errorMsg);
        setCredentialError(errorMsg);
        // Fall through to generate secure link
      } catch (err) {
        console.error('TAP generation error:', err);
        const errorMsg = `TAP error: ${err instanceof Error ? err.message : 'Unknown error'}. Falling back to Secure Link.`;
        setCredentialError(errorMsg);
        // Fall through to generate secure link
      }
    }

    // Generate secure link (either as primary choice or as fallback from TAP failure)
    await generateSecureLink(userEmail);
  };

  // Generate one-time secure link
  const generateSecureLink = async (userEmail: string) => {
    try {
      console.log('Generating secure link...');
      const linkResponse = await storeSecureCredentials({
        userName: displayName,
        userEmail,
        tempPassword: password,
        licenseName: selectedLicenseName || undefined,
        clientName,
        expiresInHours: 24,
      });

      if (linkResponse.success && linkResponse.token) {
        setCredentialMethod('secure-link');
        setSecureLinkData({
          token: linkResponse.token,
          expiresAt: linkResponse.expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        });
      } else {
        setCredentialError(linkResponse.error || 'Failed to generate secure link');
        setCredentialMethod('plain'); // Fall back to plain text as last resort
      }
    } catch (err) {
      console.error('Secure link generation error:', err);
      setCredentialError(err instanceof Error ? err.message : 'Failed to generate secure credentials');
      setCredentialMethod('plain'); // Fall back to plain text as last resort
    } finally {
      setGeneratingCredentials(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setDuplicateInfo(null);
    setResult(null);

    try {
      const userData: CreateUserRequest = {
        displayName,
        email: fullEmail,
        password,
        usageLocation,
        forceChangePasswordNextSignIn: forceChangePassword,
        licenseSkuId: selectedLicenseSkuId || undefined
      };

      const response = await createUser(clientId, userData);
      console.log('CreateUser API response:', response);
      setResult(response);

      // If license was assigned, trigger MS sync to update license counts in UI
      if (response.success && response.licenseAssigned) {
        try {
          console.log('Triggering MS sync to update license counts...');
          await syncClient(clientId, 'ms');
          console.log('MS sync completed');
        } catch (syncError) {
          console.warn('MS sync after user creation failed:', syncError);
        }
      }

      // Generate secure credentials after successful user creation
      if (response.success && response.user?.id) {
        await generateSecureCredentials(response.user.id, response.user.userPrincipalName || fullEmail);
      }
    } catch (err) {
      console.error('CreateUser error:', err);
      if (err instanceof DuplicateUserError) {
        setDuplicateInfo(err.duplicateInfo);
        if (err.duplicateInfo.foundInDeleted) {
          setError(`${fullEmail} exists in the tenant's Deleted Users recycle bin (was "${err.duplicateInfo.deletedDisplayName}").`);
        } else if (err.duplicateInfo.foundInActive) {
          setError(`${fullEmail} already exists as an active user ("${err.duplicateInfo.activeDisplayName}").`);
        } else {
          setError(`${fullEmail} already exists in this tenant (could not determine where — check Azure AD).`);
        }
      } else {
        const msg = err instanceof Error ? err.message : 'Failed to create user';
        setDuplicateInfo(null);
        if (msg.toLowerCase().includes('password') && msg.toLowerCase().includes('complexity')) {
          setError(`Password doesn't meet complexity requirements. Use at least 8 characters with uppercase, lowercase, number, and special character.`);
        } else if (msg.toLowerCase().includes('domain') && msg.toLowerCase().includes('verified')) {
          setError(`The domain "${fullEmail.split('@')[1]}" is not verified in this tenant. Add and verify it in Azure AD > Custom domain names first.`);
        } else {
          setError(msg);
        }
      }
    } finally {
      setCreating(false);
    }
  };

  // Purge deleted user from recycle bin and retry creation
  const handlePurgeAndRetry = async () => {
    if (!duplicateInfo?.deletedUserId) return;

    setPurging(true);
    setError(null);
    setDuplicateInfo(null);

    try {
      const response = await purgeAndCreateUser(clientId, {
        deletedUserId: duplicateInfo.deletedUserId,
        displayName,
        email: fullEmail,
        password,
        usageLocation,
        forceChangePasswordNextSignIn: forceChangePassword,
        licenseSkuId: selectedLicenseSkuId || undefined,
      });

      setResult(response);

      // Trigger sync if license assigned
      if (response.success && response.licenseAssigned) {
        try {
          await syncClient(clientId, 'ms');
        } catch { /* ignore sync failure */ }
      }

      // Generate secure credentials
      if (response.success && response.user?.id) {
        await generateSecureCredentials(response.user.id, response.user.userPrincipalName || fullEmail);
      }
    } catch (err) {
      console.error('PurgeAndCreate error:', err);
      setError(err instanceof Error ? err.message : 'Failed to purge and create user');
    } finally {
      setPurging(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      if (result?.success) {
        onUserCreated();
      }
      onClose();
    }
  };

  // Get the secure link URL
  const getSecureLink = () => {
    if (!secureLinkData?.token) return '';
    // Use the deployed SWA URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/credentials/${secureLinkData.token}`;
  };

  // Generate email-ready text for copying (plain text fallback)
  const getAccountInfoText = () => {
    const lines = [
      `New User Account Created`,
      `========================`,
      ``,
      `Organization: ${clientName}`,
      ``,
      `Account Details:`,
      `----------------`,
      `Name: ${displayName}`,
      `Email/Username: ${result?.user?.userPrincipalName || fullEmail}`,
    ];

    if (credentialMethod === 'tap' && tapData) {
      lines.push(`Temporary Access Pass: ${tapData.temporaryAccessPass}`);
      lines.push(`(This code can only be used once)`);
    } else if (credentialMethod === 'secure-link' && secureLinkData) {
      lines.push(`View Credentials: ${getSecureLink()}`);
      lines.push(`(This link can only be used once)`);
    } else {
      lines.push(`Temporary Password: ${password}`);
    }

    lines.push(``);
    if (result?.licenseAssigned) {
      lines.push(`License Assigned: ${selectedLicenseName}`);
    }
    lines.push(`Sign-in URL: https://portal.office.com`);
    lines.push(``);
    lines.push(`Please share these credentials securely with the user.`);

    return lines.filter(l => l !== undefined).join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getAccountInfoText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Mask phone number for privacy: +1 (9**) 5**-**64
  const maskPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits[1]}**) ${digits[4]}**-**${digits.slice(-2)}`;
    } else if (digits.length === 10) {
      return `+1 (${digits[0]}**) ${digits[3]}**-**${digits.slice(-2)}`;
    } else if (digits.length > 6) {
      return `${digits[0]}${'*'.repeat(digits.length - 3)}${digits.slice(-2)}`;
    }
    return phone;
  };

  // Handle sending email via SMTP (with optional split-channel SMS via RingCentral)
  const handleSendEmail = async () => {
    if (!recipientEmail || !result?.user?.userPrincipalName || !result?.user?.id) return;

    setSendingEmail(true);
    setEmailError(null);

    try {
      if (credentialMethod === 'tap' && tapData) {
        // Use sendUserTAP for split-channel delivery when phone is provided
        // This sends: email = instructions only, SMS = TAP code only
        if (recipientPhoneNumber && ringCentralConnected) {
          const tapResult = await sendUserTAP(
            clientId,
            result.user.id,
            recipientEmail,
            recipientPhoneNumber
          );

          if (!tapResult.success) {
            throw new Error(tapResult.error || 'Failed to send TAP');
          }
        } else {
          // Fallback: send TAP via email (less secure, but works without RingCentral)
          await sendTAPEmail({
            recipientEmail,
            userName: displayName,
            userEmail: result.user.userPrincipalName,
            temporaryAccessPass: tapData.temporaryAccessPass,
            expiresAt: tapData.expiresAt,
            licenseName: result.licenseAssigned ? selectedLicenseName : undefined,
            clientName
          });
        }
      } else if (credentialMethod === 'secure-link' && secureLinkData) {
        // Send secure link email
        await sendSecureLinkEmail({
          recipientEmail,
          userName: displayName,
          userEmail: result.user.userPrincipalName,
          secureLink: getSecureLink(),
          expiresAt: secureLinkData.expiresAt,
          licenseName: result.licenseAssigned ? selectedLicenseName : undefined,
          clientName
        });
      } else {
        // Fall back to plain credentials email (not recommended)
        const { sendCredentialsEmail } = await import('../../services/api');
        await sendCredentialsEmail({
          recipientEmail,
          userName: displayName,
          userEmail: result.user.userPrincipalName,
          tempPassword: password,
          forceChangePassword,
          licenseName: result.licenseAssigned ? selectedLicenseName : undefined,
          clientName
        });
      }
      setEmailSent(true);
    } catch (err) {
      console.error('Failed to send email:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  const isFormValid = firstName.trim() && lastName.trim() && username && selectedDomain && password;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={handleClose}></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className={`px-6 py-4 border-b border-border flex justify-between items-center ${
            result?.success
              ? 'bg-gradient-to-r from-green-600 to-green-700'
              : 'bg-gradient-to-r from-primary-600 to-primary-700'
          }`}>
            <div className="flex items-center">
              {result?.success ? (
                <CheckCircle className="h-6 w-6 text-white mr-2" />
              ) : (
                <UserPlus className="h-6 w-6 text-white mr-2" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {result?.success ? 'User Created Successfully' : 'Create New User'}
                </h3>
                <p className="text-sm text-white/80">{clientName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={creating}
              className="p-2 text-white/80 hover:text-white disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form or Success Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Success State */}
              {result?.success && (
                <div className="space-y-4">
                  {/* Credential Method Badge */}
                  {generatingCredentials ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent mr-3"></div>
                      <span className="text-sm text-muted-foreground">Generating secure credentials...</span>
                    </div>
                  ) : credentialMethod === 'tap' ? (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <span className="font-medium text-green-800 dark:text-green-200">Temporary Access Pass Generated</span>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        A secure one-time code has been created. The user will set up MFA on first sign-in.
                      </p>
                    </div>
                  ) : credentialMethod === 'secure-link' ? (
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Link className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <span className="font-medium text-purple-800 dark:text-purple-200">Secure Link Generated</span>
                      </div>
                      <p className="text-sm text-purple-700 dark:text-purple-300">
                        A one-time secure link has been created for the user to view their credentials.
                      </p>
                    </div>
                  ) : credentialError ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 p-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        <span className="text-sm text-yellow-800 dark:text-yellow-200">
                          {credentialError}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {/* Account Info Card */}
                  <div className="bg-muted rounded-lg border border-border overflow-hidden">
                    <div className="px-4 py-3 bg-muted border-b border-border">
                      <h4 className="font-medium text-foreground">Account Information</h4>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Name</span>
                        <span className="col-span-2 text-sm font-medium text-foreground">{displayName}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Email</span>
                        <span className="col-span-2 text-sm font-medium text-foreground font-mono">{result.user?.userPrincipalName}</span>
                      </div>

                      {/* Show TAP Code or Secure Link or Password based on method */}
                      {credentialMethod === 'tap' && tapData ? (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Access Pass</span>
                          <span className="col-span-2 text-sm font-medium text-foreground font-mono bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                            {tapData.temporaryAccessPass}
                          </span>
                        </div>
                      ) : credentialMethod === 'secure-link' && secureLinkData ? (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Secure Link</span>
                          <span className="col-span-2 text-xs font-medium text-purple-600 dark:text-purple-400 font-mono bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded break-all">
                            {getSecureLink()}
                          </span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Password</span>
                          <span className="col-span-2 text-sm font-medium text-foreground font-mono bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">{password}</span>
                        </div>
                      )}

                      {result.licenseAssigned && selectedLicenseName && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">License</span>
                          <span className="col-span-2 text-sm font-medium text-green-600 dark:text-green-400">{selectedLicenseName}</span>
                        </div>
                      )}
                      {result.licenseError && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">License</span>
                          <span className="col-span-2 text-sm text-yellow-600 dark:text-yellow-400">{result.licenseError}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sign-in Info */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <span className="font-medium">Sign-in URL:</span>{' '}
                          <a href="https://portal.office.com" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                            https://portal.office.com
                          </a>
                        </p>
                        {credentialMethod === 'tap' && (
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            User will enter the Access Pass instead of password and set up MFA.
                          </p>
                        )}
                        {credentialMethod === 'secure-link' && (
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            User clicks the secure link to view credentials (one-time use).
                          </p>
                        )}
                        {credentialMethod !== 'tap' && forceChangePassword && (
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            User will be prompted to change password on first sign-in.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Share Credentials Section */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Share Credentials</h4>

                    {/* Option 1: Copy to Clipboard */}
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                        copied
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                          : 'bg-card border-border text-muted-foreground hover:bg-accent hover:border-border'
                      }`}
                    >
                      {copied ? (
                        <ClipboardCheck className="h-5 w-5 flex-shrink-0" />
                      ) : (
                        <Clipboard className="h-5 w-5 flex-shrink-0" />
                      )}
                      <div className="text-left">
                        <div className="font-medium">{copied ? 'Copied!' : 'Copy to Clipboard'}</div>
                        <div className="text-xs text-muted-foreground">Copy account details to paste manually</div>
                      </div>
                    </button>

                    {/* Option 2: Send Email via SMTP */}
                    <div className="rounded-lg border border-border bg-card overflow-hidden">
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                        <Send className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                        <div className="text-left flex-1">
                          <div className="font-medium text-muted-foreground">
                            {credentialMethod === 'tap' && ringCentralConnected
                              ? 'Send Securely (Split-Channel)'
                              : credentialMethod === 'tap'
                              ? 'Send Email (TAP Code)'
                              : credentialMethod === 'secure-link'
                              ? 'Send Email (Secure Link)'
                              : 'Send Email'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {credentialMethod === 'tap' && ringCentralConnected
                              ? 'Instructions via email, TAP code via SMS'
                              : credentialMethod === 'tap'
                              ? 'Send TAP code via email (connect RingCentral for SMS)'
                              : credentialMethod === 'secure-link'
                              ? 'Send one-time secure link via email'
                              : 'Send credentials via email'}
                          </div>
                        </div>
                        {credentialMethod === 'tap' && ringCentralConnected && (
                          <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                            SMS Ready
                          </div>
                        )}
                      </div>

                      {emailSent ? (
                        <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20">
                          <div className="flex flex-col gap-1 text-green-700 dark:text-green-300">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-5 w-5" />
                              <span className="font-medium">
                                {credentialMethod === 'tap' && recipientPhoneNumber && ringCentralConnected
                                  ? 'Sent securely via split-channel!'
                                  : `Email sent to ${recipientEmail}`}
                              </span>
                            </div>
                            {credentialMethod === 'tap' && recipientPhoneNumber && ringCentralConnected && (
                              <div className="text-xs ml-7 space-y-1">
                                <div>📧 Instructions sent to <strong>{recipientEmail}</strong></div>
                                <div>📱 TAP code sent via SMS to <strong>{maskPhoneNumber(recipientPhoneNumber)}</strong></div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="px-4 py-3 space-y-3">
                          {/* Email input */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">
                              Recipient Email (for instructions)
                            </label>
                            <input
                              type="email"
                              value={recipientEmail}
                              onChange={(e) => setRecipientEmail(e.target.value)}
                              placeholder="supervisor@company.com"
                              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>

                          {/* Phone input - only show for TAP when RingCentral connected */}
                          {credentialMethod === 'tap' && ringCentralConnected && (
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">
                                User's Phone Number (for TAP code via SMS)
                              </label>
                              <input
                                type="tel"
                                value={recipientPhoneNumber}
                                onChange={(e) => setRecipientPhoneNumber(e.target.value)}
                                placeholder="+1 (555) 123-4567"
                                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                TAP code will be sent via SMS (displayed as {recipientPhoneNumber ? maskPhoneNumber(recipientPhoneNumber) : '+1 (5**) 1**-**67'} in email)
                              </p>
                            </div>
                          )}

                          {/* Show hint if RingCentral not connected */}
                          {credentialMethod === 'tap' && !ringCentralConnected && (
                            <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                              💡 Connect RingCentral in My Settings (user menu) to enable secure split-channel delivery (TAP code via SMS)
                            </div>
                          )}

                          {/* Send button */}
                          <button
                            type="button"
                            onClick={handleSendEmail}
                            disabled={!recipientEmail || sendingEmail || generatingCredentials}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {sendingEmail ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4" />
                                {credentialMethod === 'tap' && recipientPhoneNumber && ringCentralConnected
                                  ? 'Send Email + SMS'
                                  : 'Send Email'}
                              </>
                            )}
                          </button>
                          {emailError && (
                            <p className="text-xs text-red-600 dark:text-red-400">{emailError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-red-700 dark:text-red-300">{error}</p>
                      {duplicateInfo?.foundInDeleted && duplicateInfo.deletedUserId && (
                        <button
                          type="button"
                          onClick={handlePurgeAndRetry}
                          disabled={purging}
                          className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {purging ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Purging & creating...
                            </>
                          ) : (
                            'Purge from Recycle Bin & Create User'
                          )}
                        </button>
                      )}
                      {duplicateInfo?.foundInActive && !duplicateInfo?.foundInDeleted && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                          This user is active in the tenant. Use a different email address or manage the existing user from the Users tab.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {domainsError && !result?.success && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
                    <p className="text-yellow-700 dark:text-yellow-300">
                      Could not load domains: {domainsError}. Please ensure the tenant has GDAP consent configured.
                    </p>
                  </div>
                </div>
              )}

              {/* Form Fields */}
              {!result?.success && (
                <>
                  {/* TAP Eligibility Badge */}
                  {!loadingSubscriptions && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      isTapEligible
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                        : 'bg-muted text-muted-foreground border border-border'
                    }`}>
                      {isTapEligible ? (
                        <>
                          <ShieldCheck className="h-4 w-4" />
                          <span>Secure onboarding available (TAP eligible)</span>
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4" />
                          <span>Secure link will be used for credential delivery</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* First Name and Last Name in a row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        required
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Smith"
                        required
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Display Name (auto-generated, read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      readOnly
                      className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-foreground cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-generated from first and last name
                    </p>
                    {duplicateNameMatches.length > 0 && (
                      <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <div className="flex items-start">
                          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                              Duplicate name detected
                            </p>
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              {duplicateNameMatches.length === 1 ? 'A user' : `${duplicateNameMatches.length} users`} with the name "{displayName}" already {duplicateNameMatches.length === 1 ? 'exists' : 'exist'}:
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {duplicateNameMatches.map((u, i) => (
                                <li key={i} className="text-xs text-amber-600 dark:text-amber-400">
                                  • {u.userPrincipalName}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Username and Domain */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Email Address *
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                          placeholder="john.smith"
                          required
                          className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <span className="flex items-center text-muted-foreground">@</span>
                      <div className="flex-1">
                        {loadingDomains ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground bg-muted rounded-lg border border-border">
                            Loading...
                          </div>
                        ) : domains.length === 0 ? (
                          <input
                            type="text"
                            value={selectedDomain}
                            onChange={(e) => setSelectedDomain(e.target.value)}
                            placeholder="domain.com"
                            required
                            className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        ) : (
                          <select
                            value={selectedDomain}
                            onChange={(e) => setSelectedDomain(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            {domains.map(domain => (
                              <option key={domain.domain} value={domain.domain}>
                                {domain.domain}{domain.isDefault ? ' (default)' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                    {fullEmail && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Full email: <span className="font-medium">{fullEmail}</span>
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Temporary Password
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="flex-1 px-3 py-2 border border-border rounded-lg bg-card text-foreground font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setPassword(generatePassword())}
                        className="px-3 py-2 text-sm bg-muted text-foreground rounded-lg hover:bg-accent"
                      >
                        Generate
                      </button>
                    </div>
                    {selectedDeliveryMethod === 'tap' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        TAP selected - user will use a one-time access pass instead
                      </p>
                    )}
                  </div>

                  {/* Credential Delivery Method */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      Credential Delivery Method
                    </label>
                    <div className="space-y-2">
                      <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedDeliveryMethod === 'tap'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-border hover:bg-accent'
                      } ${!isTapEligible || !tenantId ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="tap"
                          checked={selectedDeliveryMethod === 'tap'}
                          onChange={() => setSelectedDeliveryMethod('tap')}
                          disabled={!isTapEligible || !tenantId}
                          className="mt-0.5 h-4 w-4 text-green-600 focus:ring-green-500"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-foreground">
                            Temporary Access Pass (TAP)
                          </span>
                          <p className="text-xs text-muted-foreground">
                            One-time code, forces MFA setup. Requires Entra ID P1/P2.
                          </p>
                          {!isTapEligible && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              Not available - no eligible license detected
                            </p>
                          )}
                          {isTapEligible && !tenantId && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              Not available - no Tenant ID configured
                            </p>
                          )}
                        </div>
                      </label>

                      <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedDeliveryMethod === 'secure-link'
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-border hover:bg-accent'
                      }`}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="secure-link"
                          checked={selectedDeliveryMethod === 'secure-link'}
                          onChange={() => setSelectedDeliveryMethod('secure-link')}
                          className="mt-0.5 h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-foreground">
                            Secure One-Time Link
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Password delivered via one-time secure link. Works with all tenants.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Force Change Password - only relevant when using secure link */}
                  {selectedDeliveryMethod === 'secure-link' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="forceChange"
                        checked={forceChangePassword}
                        onChange={(e) => setForceChangePassword(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-border rounded"
                      />
                      <label htmlFor="forceChange" className="ml-2 text-sm text-muted-foreground">
                        Force password change on first sign-in
                      </label>
                    </div>
                  )}

                  {/* Usage Location */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Usage Location
                    </label>
                    <select
                      value={usageLocation}
                      onChange={(e) => setUsageLocation(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="GU">Guam</option>
                      <option value="PH">Philippines</option>
                      <option value="PW">Palau</option>
                      <option value="MP">Northern Mariana Islands</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Required for license assignment
                    </p>
                  </div>

                  {/* License Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Assign License (Optional)
                    </label>
                    {loadingSubscriptions ? (
                      <div className="text-sm text-muted-foreground">Loading available licenses...</div>
                    ) : availableLicenses.length > 0 ? (
                      <select
                        value={selectedLicenseSkuId}
                        onChange={handleLicenseChange}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">No license</option>
                        {availableLicenses.map(lic => (
                          <option key={lic.msSkuId} value={lic.msSkuId}>
                            {lic.productName} ({lic.availableLicenses} available)
                          </option>
                        ))}
                      </select>
                    ) : subscriptions.length > 0 ? (
                      <select
                        value={selectedLicenseSkuId}
                        onChange={handleLicenseChange}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">No license</option>
                        {subscriptions.map(sub => {
                          const available = sub.quantity - (sub.assignedQuantity || 0);
                          return (
                            <option key={sub.msSkuId} value={sub.msSkuId}>
                              {sub.productName.replace(/\[New Commerce Experience\]/gi, '').trim()} ({available} available)
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <div className="text-sm text-yellow-600 dark:text-yellow-400">
                        No available licenses. All licenses are assigned.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border bg-muted flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                {result?.success ? 'Close' : 'Cancel'}
              </button>
              {!result?.success && (
                <button
                  type="submit"
                  disabled={creating || !isFormValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create User
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
